import { Check } from 'lucide-react';
import { useLoaderData } from 'react-router';

import { cn } from '@rocco/utils/cn';
import {
    Drawer,
    DrawerContent,
    DrawerDescription,
    DrawerTitle,
} from '@rocco/components/drawer';
import { CLEARCUT_FILL_COLOR } from '@rocco/components/image';
import { useVariantSelectionState } from '@modules/product/context/VariantSelectionContext';
import type { loader } from '~/routes/_app.product.$urlSegment';
import { detailCta } from '~/ui-rocco/pages/detail-shared';
import { PILL } from './purchase-flow-parts';

/** Fallback background for multiplied/clear-cut studio images (matches the
 *  desktop purchase drawer / PhotoViewerModal). */
const MULTIPLIED_IMAGE_BG = CLEARCUT_FILL_COLOR;

export interface MobileCartConfirmSheetProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

/**
 * The mobile added-to-cart confirmation — a bottom sheet with the selected
 * variant's image, an "Added to cart" confirmation and a View Cart / Continue
 * Shopping choice. Replaces the top-nav cart preview (a desktop pattern) for
 * every mobile add path: the inline CTA, the variant sheet and the island's
 * purchase pill. Requires the variant selection provider above it.
 */
export const MobileCartConfirmSheet = ({
    open,
    onOpenChange,
}: MobileCartConfirmSheetProps) => {
    const { product } = useLoaderData<typeof loader>();
    const { selectedVariant } = useVariantSelectionState();

    // The selected variant's image (falling back to the first product image),
    // matching the desktop purchase drawer's thumbnail logic — multiplied /
    // clear-cut studio shots sit contained on a soft background.
    const variantImage =
        (selectedVariant?.Image &&
            product.LibraryImages.find(
                (image) => image.ID === selectedVariant.Image?.ID,
            )) ||
        product.LibraryImages[0];
    const imageSrc =
        variantImage?.LibraryImageFile?.DetailPageSquare.Desktop ?? '';
    const useSoftBackground = Boolean(
        variantImage?.IsMultiplied || variantImage?.IsClearCut,
    );
    const imageBackgroundColor =
        variantImage?.LibraryImageFile?.ImageBackgroundColor ??
        MULTIPLIED_IMAGE_BG;

    return (
        <Drawer open={open} onOpenChange={onOpenChange} direction="bottom">
            <DrawerContent noHandle>
                <DrawerDescription className="hidden" aria-hidden />
                <div className="flex flex-col items-center px-4 pt-7 pb-6 text-center">
                    {imageSrc ? (
                        <div
                            className="flex size-24 items-center justify-center overflow-hidden rounded-tile-card"
                            style={{
                                backgroundColor: useSoftBackground
                                    ? imageBackgroundColor
                                    : '#f3f4f6',
                            }}
                        >
                            <img
                                src={imageSrc}
                                alt={product.Title}
                                className={cn(
                                    'size-full',
                                    useSoftBackground
                                        ? 'object-contain mix-blend-multiply'
                                        : 'object-cover',
                                )}
                            />
                        </div>
                    ) : null}
                    <DrawerTitle className="mt-4 flex items-center gap-2 font-sans text-xl leading-[1.2] font-medium tracking-[-0.02em] text-[#181818]">
                        <span className="flex size-6 items-center justify-center rounded-full bg-[#181818]">
                            <Check
                                className="size-3.5 text-white"
                                strokeWidth={3}
                            />
                        </span>
                        Added to cart
                    </DrawerTitle>
                    <p className="m-0 mt-1.5 line-clamp-2 font-sans text-sm text-[#7f7f7f]">
                        {product.Title}
                    </p>
                    <div className="mt-6 flex w-full flex-col gap-3">
                        <a
                            href="/shopping-cart"
                            className={PILL('filled-light')}
                        >
                            View Cart
                        </a>
                        <button
                            type="button"
                            onClick={() => onOpenChange(false)}
                            className={detailCta(
                                'outlined-light',
                                'h-[3.125rem] w-full text-sm',
                            )}
                        >
                            Continue Shopping
                        </button>
                    </div>
                </div>
            </DrawerContent>
        </Drawer>
    );
};
